import {
  PropertyGroupDetails,
  PropertyGroupPermissions,
  PropertyGroupProperties
} from '@hortplus/properties-react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import LoadingOverlay from '../../LoadingOverlay';
import ConfirmationModal from '../../helpers/ConfirmationModal';
import { useFirstRender } from '../../hooks/FirstRenderHook';
import { useStores } from '../../stores/useStores';
import Passcode from './Passcode';

function TrappingRun(props) {
  const { authenticationStore } = useStores();
  const [loadingSelectedPropertyGroup, setLoadingSelectedPropertyGroup] =
    useState(false);
  const firstRender = useFirstRender();
  // Custom options.
  const customOptions = props?.customOptions;
  const customDetailOptions = customOptions?.details;
  const customPasscodeOptions = customOptions?.passcode;
  const customHardDeleteOption = customOptions?.disableHardDelete;

  const selectedTrappingRun = props.selectedTrappingRun;
  const propertyGroup = selectedTrappingRun.property_group;

  useEffect(() => {
    if (!firstRender) props.history.replace('/trapping/runs');
  }, [props.selectedDatabase]);

  useEffect(() => {
    if (propertyGroup && (!propertyGroup.properties || !propertyGroup.users)) {
      setLoadingSelectedPropertyGroup(true);
      fetch(
        `${process.env.REACT_APP_PROPERTIES_API_URL}api/property-groups/${propertyGroup.id}?append[]=userProperty&with[]=properties&with[]=users.propertyRole&withTrashed=true`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${authenticationStore.bearerToken}`,
            SeasonalDatabase: authenticationStore.selectedDatabase
          }
        }
      )
        .then((res) => {
          if (res.ok) return res.json();
          else if (res.status === 401) props.refreshBearerToken();
          else throw Error(res.statusText);
        })
        .then((data) => {
          if (data) {
            updatePropertyGroup(data);
          }
        })
        .catch((err) => {
          if (process.env.NODE_ENV === 'development') console.error(err);
        })
        .finally(() => {
          setLoadingSelectedPropertyGroup(false);
        });
    }
  }, [props.bearerToken, props.selectedDatabase, propertyGroup]);

  const updatePropertyGroup = (propertyGroup, isDelete) => {
    const newTrappingRun = { ...selectedTrappingRun };
    newTrappingRun.property_group = propertyGroup;
    props.update(newTrappingRun, isDelete);
  };

  /**
   * Restore the deleted property using the properties API and update the property object in the
   * parent component using the update() method supplied in the props.
   */
  const restore = (trappingRun) => {
    return fetch(
      `${process.env.REACT_APP_PROPERTIES_API_URL}api/property-groups/${trappingRun.property_group_id}/restore?with[]=properties&with[]=users.propertyRole&append[]=userProperty`,
      {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + authenticationStore.bearerToken,
          SeasonalDatabase: props.selectedDatabase
        }
      }
    )
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) props.refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          trappingRun.property_group = data;
          props.update(trappingRun);
        }
      });
  };

  const permanentlyDelete = (trappingRun) => {
    return fetch(
      `${process.env.REACT_APP_TRAPLOG_API_URL}api/trapping-runs/${trappingRun.id}`,
      {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + authenticationStore.bearerToken,
          SeasonalDatabase: props.selectedDatabase
        }
      }
    ).then((res) => {
      if (res.ok) {
        // Remove property group from state.
        props.update(trappingRun, true);
      } else if (res.status === 401) props.refreshBearerToken();
      else throw Error(res.statusText);
    });
  };

  return loadingSelectedPropertyGroup ? (
    <LoadingOverlay />
  ) : !propertyGroup.properties || !propertyGroup.users ? null : (
    <Fragment>
      <Row>
        <Col xs={2}>
          <Button variant='outline-secondary' onClick={props.close}>
            Back
          </Button>
        </Col>
        {!customHardDeleteOption &&
        propertyGroup.userProperty.can_edit &&
        propertyGroup.deleted_at ? (
          <>
            <Col xs={10} md={4}>
              <ConfirmationModal
                title={'Confirm Delete'}
                bodyText={
                  'Are you sure you want to permanently delete this trapping run?'
                }
                showButton={{
                  children: 'Permanently Delete',
                  props: {
                    className: 'float-right ml-1',
                    variant: 'outline-danger'
                  }
                }}
                confirmButton={{ props: { variant: 'danger' } }}
                confirmAction={() => {
                  return permanentlyDelete(selectedTrappingRun);
                }}
              />
              <ConfirmationModal
                title={'Confirm Restore'}
                bodyText={'Are you sure you want to restore this trapping run?'}
                showButton={{
                  children: 'Restore',
                  props: {
                    className: 'float-right',
                    variant: 'outline-success'
                  }
                }}
                confirmButton={{ props: { variant: 'success' } }}
                confirmAction={() => {
                  return restore(selectedTrappingRun);
                }}
              />
            </Col>
          </>
        ) : (
          <></>
        )}
        <Col xs={0} md={6}></Col>
      </Row>
      <Row className='mt-2'>
        <Col>
          <Row>
            <Col>
              <PropertyGroupDetails
                bearerToken={authenticationStore.bearerToken}
                selectedDatabase={authenticationStore.selectedDatabase}
                refreshBearerToken={authenticationStore.refreshBearerToken}
                propertyGroup={propertyGroup}
                update={updatePropertyGroup}
                customOptions={customDetailOptions}
              />
            </Col>
          </Row>
          {props.permissionsCard && (
            <Row className='mt-4'>
              <Col>
                <PropertyGroupPermissions
                  bearerToken={authenticationStore.bearerToken}
                  selectedDatabase={authenticationStore.selectedDatabase}
                  refreshBearerToken={authenticationStore.refreshBearerToken}
                  propertyGroup={propertyGroup}
                  update={updatePropertyGroup}
                  users={props.users}
                  propertyRoles={props.propertyRoles}
                />
              </Col>
            </Row>
          )}
          <Row className='mt-4'>
            <Col>
              <Passcode
                trappingRun={selectedTrappingRun}
                update={props.update}
                customOptions={customPasscodeOptions}
              />
            </Col>
          </Row>
        </Col>
        <Col>
          <PropertyGroupProperties
            bearerToken={authenticationStore.bearerToken}
            selectedDatabase={authenticationStore.selectedDatabase}
            refreshBearerToken={authenticationStore.refreshBearerToken}
            propertyGroup={propertyGroup}
            update={updatePropertyGroup}
          />
        </Col>
      </Row>
    </Fragment>
  );
}

TrappingRun.propTypes = {
  permissionsCard: PropTypes.bool,
  selectedDatabase: PropTypes.string.isRequired,
  selectedTrappingRun: PropTypes.object.isRequired,
  update: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
  propertyRoles: PropTypes.array.isRequired,
  users: PropTypes.array.isRequired
};

export default observer(TrappingRun);
